import { CalendarOutlined } from '@ant-design/icons';
import { AlertBox, DatePicker, RadioGroup, TextInput } from '@life-line-lab/components-react';
import { Col, Row } from 'antd';
import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { ShallowClonedTypeFor } from 'Types/common.types';
import CountrySelector from '../country-selector/country-selector';
// import { getIdentityTypeOptions } from 'Data/identity-type-options';

export type IdentityNumberFormInput = {
  identificationType: string;
  identificationNumber: string;
  identificationIssueDate: string;
  identificationExpiryDate: string;
  identificationPlaceOfIssue: string;
};
type Props = {
  isRequired?: boolean;
  isReadonly?: boolean;
  input: IdentityNumberFormInput;
  error: ShallowClonedTypeFor<IdentityNumberFormInput, string>;
  handleSelectChange: (value: any, name: keyof IdentityNumberFormInput) => void;
};

const IdentityNumberInput = ({ isRequired, input, error, handleSelectChange, isReadonly }: Props) => {
  const { t } = useTranslation();
  
  const getIdentityTypeOptions = () => [
    { value: 'NRIC', label: t('option.nric') },
    { value: 'PASSPORT', label: t('option.passport') },
  ];

  const identityTypeOptions = getIdentityTypeOptions();

  return (
    <div>
      <div>
        <RadioGroup
          direction="vertical"
          isRequired={isRequired}
          isReadonly={isReadonly}
          isError={!!error.identificationType}
          label={t('label.identificationType')}
          name="identificationType"
          value={input.identificationType}
          onChange={(e: any) => handleSelectChange(e.target.value, 'identificationType')}
          options={identityTypeOptions}
          />
        <AlertBox text={error.identificationType} />
      </div>

      <div>
        <TextInput
          isRequired={isRequired}
          isReadonly={isReadonly}
          isError={!!error.identificationNumber}
          label={
            input.identificationType == 'NRIC'
            ? t('label.identificationNumber')
            : t('label.passportNumber')
          }
          name="identificationNumber"
          value={input.identificationNumber}
          onChange={(e: any) => handleSelectChange(e.target.value, 'identificationNumber')}
          />
        <AlertBox text={error.identificationNumber} />
      </div>

      {input.identificationType == 'PASSPORT' && (
        <React.Fragment>
          <Row gutter={16}>
            <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
              <div>
                <DatePicker
                  isRequired={isRequired}
                  isDisabled={isReadonly}
                  isError={!!error.identificationIssueDate}
                  suffix={<CalendarOutlined />}
                  label={t('label.identificationIssueDate')}
                  placeholder="YYYY-MM-DD"
                  value={
                    input.identificationIssueDate ? moment(input.identificationIssueDate) : null
                  }
                  disabledDate={(currentDate: any) => {
                    return currentDate && currentDate.valueOf() >= Date.now();
                  }}
                  onChange={(value: any) => handleSelectChange(value, 'identificationIssueDate')}
                  />
                <AlertBox text={error.identificationIssueDate} />
              </div>
            </Col>

            <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
              <div>
                <DatePicker
                  isRequired={isRequired}
                  isDisabled={isReadonly}
                  isError={!!error.identificationExpiryDate}
                  suffix={<CalendarOutlined />}
                  label={t('label.identificationExpiryDate')}
                  placeholder="YYYY-MM-DD"
                  value={
                    input.identificationExpiryDate ? moment(input.identificationExpiryDate) : null
                  }
                  disabledDate={(currentDate: any) => {
                    return currentDate && currentDate.valueOf() < Date.now();
                  }}
                  onChange={(value: any) => handleSelectChange(value, 'identificationExpiryDate')}
                  />
                <AlertBox text={error.identificationExpiryDate} />
              </div>
            </Col>
          </Row>

          <div>
            <CountrySelector
              isRequired={isRequired}
              isReadonly={isReadonly}
              isError={!!error.identificationPlaceOfIssue}
              label={t('label.identificationPlaceOfIssue')}
              name="identificationPlaceOfIssue"
              value={input.identificationPlaceOfIssue}
              onChange={(value: string) => handleSelectChange(value, 'identificationPlaceOfIssue')}
            />
            <AlertBox text={error.identificationPlaceOfIssue} />
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default IdentityNumberInput;
